// -----------------------------------------------------------------------------
// <copyright file="token-interceptor.interceptor.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalInterceptorAuthRequest,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserConfigurationAuthError,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import { EMPTY, Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { GlobalService } from '../../shared/services/global.service';
import { LoginService } from '../services/login.service';
import { LoaderService } from '../../shared/loader/loader.service';
import { ModalService } from '../../shared/modals/modal.service';
import { BASE_API_URL, PENDO_BASE_API_URL } from '../../shared/constants/global';
import { environment } from '../../../environments/environment';
import { IframeAuthService } from '../services/iframe-auth';
import { isInsideIframe } from '../../iframe-helper';
import { VcDataService } from '../../features/configurator/service/vc-data';

const IS_IFRAME = isInsideIframe();

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    public http: HttpClient,
    private globalService: GlobalService,
    private vcDataService: VcDataService,
    @Inject(MSAL_INTERCEPTOR_CONFIG)
    private msalInterceptorConfig: MsalInterceptorConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loginService: LoginService,
    private loaderService: LoaderService,
    private router: Router,
    private modalService: ModalService,
    private iframeAuth: IframeAuthService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.indexOf(PENDO_BASE_API_URL) > -1) {
      request = request.clone({
        setHeaders: {
          'x-pendo-integration-key': 'e00a05f2-6a42-486b-5243-d7bf240ae4d0.us',
        },
      });
    } else if (request.url.indexOf(BASE_API_URL) > -1) {
      if (request.method !== 'PUT') {
        if (IS_IFRAME) {
          return this.handleIframeRequest(request, next);
        }

        // STANDALONE PATH — original MSAL flow
        if (
          this.msalInterceptorConfig.interactionType !== InteractionType.Popup &&
          this.msalInterceptorConfig.interactionType !== InteractionType.Redirect
        ) {
          throw new BrowserConfigurationAuthError(
            'invalid_interaction_type',
            'Invalid interaction type provided to MSAL Interceptor. InteractionType.Popup, InteractionType.Redirect must be provided in the msalInterceptorConfiguration',
          );
        }

        // Sets account as active account or first account
        let account: any;
        // eslint-disable-next-line no-extra-boolean-cast
        if (!!this.authService.instance.getActiveAccount()) {
          account = this.authService.instance.getActiveAccount();
        } else {
          account = this.authService.instance.getAllAccounts()[0];
        }

        const scopes = environment.apiConfig.scopes;

        const authRequest =
          typeof this.msalInterceptorConfig.authRequest === 'function'
            ? this.msalInterceptorConfig.authRequest(this.authService, request, {
                account: account,
              })
            : { ...this.msalInterceptorConfig.authRequest, account };

        return this.acquireToken(authRequest, scopes, account).pipe(
          switchMap((result: AuthenticationResult) => {
            request = request.clone({
              setHeaders: {
                Authorization: `Bearer ${result.accessToken}`,
                'ngrok-skip-browser-warning': '6024',
              },
            });

            if (this.globalService.getDataFromStorage('selected-business-unit', true, false)) {
              request = request.clone({
                setHeaders: {
                  'Business-Unit-Id': this.globalService.getDataFromStorage(
                    'selected-business-unit',
                    true,
                    false,
                  ),
                },
              });
            }

            if (request.method === 'PATCH') {
              request = request.clone({
                setHeaders: {
                  Prefer: 'return=representation',
                },
              });
            }

            if (['POST', 'PATCH'].includes(request.method) && request.body) {
              request = request.clone({
                body: this.globalService.convertEmptyStringsToNull(request.body),
              });
            }

            return next.handle(request).pipe(
              map((event) => event),
              catchError((error) => this.handleHttpError(error, request)),
            );
          }),
        );
      }
    }

    return next.handle(request);
  }

  // IFRAME REQUEST HANDLER — uses token passed from host
  private handleIframeRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.iframeAuth.getToken();

    if (!token) {
      this.loaderService.setLoadingState.set(false);
      return observableThrowError(() => ({
        message: 'No host token available',
      }));
    }
    const buId = this.globalService.getDataFromStorage('Business-Unit-Id', true, true);
    const buName = this.globalService.getDataFromStorage('BusinessUnit-Name', true, true);
    const emailId = this.globalService.getDataFromStorage('Email-Id', true, true);
    const salesLocationCode = this.globalService.getDataFromStorage('Sales-Location-Code');
    const salesLocationId =
      this.vcDataService.getSalesLocationByCode(salesLocationCode)?.SalesLocationId ?? '';
    if (buName) {
      request = request.clone({
        setHeaders: {
          'BusinessUnit-Name': buName,
        },
      });
    }
    if (buId) {
      request = request.clone({
        setHeaders: {
          'Business-Unit-Id': buId,
        },
      });
    }
    if (emailId) {
      request = request.clone({
        setHeaders: {
          'Email-Id': emailId,
        },
      });
    }
    if (salesLocationId) {
      request = request.clone({
        setHeaders: {
          'Default-Sales-Location-Id': salesLocationId,
        },
      });
    }
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': '6024',
      },
    });

    if (request.method === 'PATCH') {
      request = request.clone({
        setHeaders: { Prefer: 'return=representation' },
      });
    }

    if (['POST', 'PATCH'].includes(request.method) && request.body) {
      request = request.clone({
        body: this.globalService.convertEmptyStringsToNull(request.body),
      });
    }

    return next.handle(request).pipe(
      map((event) => event),
      catchError((error) => this.handleHttpError(error, request)),
    );
  }

  // SHARED HTTP ERROR HANDLER (extracted from original catchError)
  private handleHttpError(error: any, request: HttpRequest<any>): Observable<never> {
    if (error.status === 401) {
      //   if (
      //   error.error &&
      //   (error.error.message === 'refreshTokenExpired' || error.error.message === 'roleMissing')
      // ) {
      // }
      this.loaderService.setLoadingState.set(false);
      // this.globalService.clearStorage();
      //  Don't navigate inside iframe — ask host to refresh instead
      if (IS_IFRAME) {
        this.requestTokenRefreshFromHost();
      } else {
        this.router.navigate(['/']);
      }
      return EMPTY as Observable<never>;
    } else if (error.status === 403 && error.error && error.error.code === 'Forbidden') {
      this.loaderService.setLoadingState.set(false);
      const dialog = this.modalService
        .openCustomWarningDialog({
          message: request.url?.includes('ResetPasswordFromToken') ? 'q.el' : 'q.ms',
          btnTxt2: 'cmn.ok',
        })
        .subscribe(() => {
          dialog.unsubscribe();
        });
      return observableThrowError(() => error.error);
    } else if (error.status === 417 && error.error && error.error.code === 'ServiceLevelChecks') {
      this.loaderService.setLoadingState.set(false);
      const dialog = this.modalService
        .openCustomWarningDialog({ message: error.error.message, btnTxt2: 'cmn.ok' })
        .subscribe(() => {
          dialog.unsubscribe();
          this.loaderService.setLoadingState.set(true);
          window.location.reload();
        });
      return observableThrowError(() => error.error);
    } else if (
      error.error &&
      (error.error.message === 'refreshTokenExpired' || error.error.message === 'roleMissing')
    ) {
      this.globalService.clearStorage();
      this.loaderService.setLoadingState.set(false);

      if (!IS_IFRAME) {
        window.location.href = location.origin;
      }
      return observableThrowError(() => error.error);
    } else if (error.error && error.error.Message === 'BaseApiNotResponding') {
      const err = document.getElementsByClassName('error-handle');
      if (err && err[0]) {
        err[0].classList.remove('hide-err');
        err[0].classList.add('show-err');
        setTimeout(() => {
          err[0].classList.add('hide-err');
        }, 10000);
      }
      this.loaderService.setLoadingState.set(false);
      return observableThrowError(() => error.error);
    } else {
      if (error.statusText === 'Unknown Error' || error.statusText === 'Internal Server Error') {
        const err = document.getElementsByClassName('error-handle');
        if (err && err[0]) {
          err[0].classList.remove('hide-err');
          err[0].classList.add('show-err');
          setTimeout(() => {
            err[0].classList.add('hide-err');
          }, 10000);
        }
      }
      this.loaderService.setLoadingState.set(false);
      return observableThrowError(() => (error.error ? error.error : error));
    }
  }

  //  Ask host to send a fresh token (on 401 inside iframe)
  private requestTokenRefreshFromHost(): void {
    try {
      window.parent.postMessage(
        {
          type: 'REQUEST_TOKEN_REFRESH',
          payload: {
            // scopes: environment.apiConfig.scopes,
          },
        },
        '*',
      );
    } catch (e) {
      console.error('Failed to request token refresh from host:', e);
    }
  }

  // STANDALONE — original MSAL token acquisition
  private acquireToken(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
    account: AccountInfo,
  ): Observable<AuthenticationResult> {
    return this.authService.acquireTokenSilent({ ...authRequest, scopes, account }).pipe(
      catchError(() => {
        this.authService
          .getLogger()
          .error(
            'Interceptor - acquireTokenSilent rejected with error. Invoking interaction to resolve.',
            'error',
          );
        return this.msalBroadcastService.inProgress$.pipe(
          take(1),
          switchMap((status: InteractionStatus) => {
            if (status === InteractionStatus.None) {
              return this.acquireTokenInteractively(authRequest, scopes);
            }

            return this.msalBroadcastService.inProgress$.pipe(
              filter((status: InteractionStatus) => status === InteractionStatus.None),
              take(1),
              switchMap(() => this.acquireToken(authRequest, scopes, account)),
            );
          }),
        );
      }),
      switchMap((result: AuthenticationResult) => {
        if (!result.accessToken) {
          this.authService
            .getLogger()
            .error(
              'Interceptor - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.',
              'error',
            );
          return this.msalBroadcastService.inProgress$.pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            take(1),
            switchMap(() => this.acquireTokenInteractively(authRequest, scopes)),
          );
        }
        return of(result);
      }),
    );
  }

  private acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
  ): Observable<AuthenticationResult> {
    if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
      this.authService
        .getLogger()
        .verbose('Interceptor - error acquiring token silently, acquiring by popup', 'error');
      return this.authService.acquireTokenPopup({ ...authRequest, scopes });
    }
    this.authService
      .getLogger()
      .verbose('Interceptor - error acquiring token silently, acquiring by redirect', 'error');
    const redirectStartPage = window.location.href;
    this.authService.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }
}
